/**
 * Member Field Mapping
 * 
 * เทียบ field ระหว่าง UI Model กับ Parse Schema
 */

import { FieldMap } from './types';

/**
 * Member Field Map
 * 
 * UI Field           | Parse V1              | Parse V2
 * -------------------|----------------------|------------------------
 * id                 | objectId             | objectId
 * username           | username             | username
 * email              | email                | email
 * phone              | phone                | phoneNumber
 * balance            | balance              | wallet.balance
 * totalDeposit       | total_deposit        | stats.totalDeposit
 * totalWithdraw      | total_withdraw       | stats.totalWithdraw
 * status             | status               | accountStatus
 * lastLogin          | last_login           | activity.lastLogin
 * referralCode       | referral_code        | referral.code
 * affiliateId        | affiliate_id         | referral.affiliateId
 * vipLevel           | vip_level            | membership.vipLevel
 * createdAt          | createdAt            | createdAt
 * updatedAt          | updatedAt            | updatedAt
 */
export const memberFieldMap: FieldMap = {
  id: {
    uiField: 'id',
    parseV1Field: 'objectId',
    parseV2Field: 'objectId',
    required: true,
  },
  username: {
    uiField: 'username',
    parseV1Field: 'username',
    parseV2Field: 'username',
    required: true,
  },
  email: {
    uiField: 'email',
    parseV1Field: 'email',
    parseV2Field: 'email',
    required: true,
  },
  phone: {
    uiField: 'phone',
    parseV1Field: 'phone',
    parseV2Field: 'phoneNumber',
    required: true,
  },
  balance: {
    uiField: 'balance',
    parseV1Field: 'balance',
    parseV2Field: 'wallet.balance',
    defaultValue: 0,
    fromParse: (value) => Number(value) || 0,
  },
  totalDeposit: {
    uiField: 'totalDeposit',
    parseV1Field: 'total_deposit',
    parseV2Field: 'stats.totalDeposit',
    defaultValue: 0,
    fromParse: (value) => Number(value) || 0,
  },
  totalWithdraw: {
    uiField: 'totalWithdraw',
    parseV1Field: 'total_withdraw',
    parseV2Field: 'stats.totalWithdraw',
    defaultValue: 0,
    fromParse: (value) => Number(value) || 0,
  },
  status: {
    uiField: 'status',
    parseV1Field: 'status',
    parseV2Field: 'accountStatus',
    defaultValue: 'active',
    fromParse: (value, version) => {
      // Handle status mapping differences
      if (version === 'v1') {
        const statusMap: Record<string, string> = {
          '1': 'active',
          '0': 'inactive',
          '-1': 'banned',
        };
        return statusMap[String(value)] || value;
      }
      return value;
    },
    toParse: (value, version) => {
      if (version === 'v1') {
        const statusMap: Record<string, string> = {
          active: '1',
          inactive: '0',
          banned: '-1',
        };
        return statusMap[String(value)] || value;
      }
      return value;
    },
  },
  lastLogin: {
    uiField: 'lastLogin',
    parseV1Field: 'last_login',
    parseV2Field: 'activity.lastLogin',
    fromParse: (value) => value ? new Date(value as string).toISOString() : undefined,
  },
  referralCode: {
    uiField: 'referralCode',
    parseV1Field: 'referral_code',
    parseV2Field: 'referral.code',
  },
  affiliateId: {
    uiField: 'affiliateId',
    parseV1Field: 'affiliate_id',
    parseV2Field: 'referral.affiliateId',
  },
  vipLevel: {
    uiField: 'vipLevel',
    parseV1Field: 'vip_level',
    parseV2Field: 'membership.vipLevel',
    defaultValue: 0,
    fromParse: (value) => Number(value) || 0,
  },
  createdAt: {
    uiField: 'createdAt',
    parseV1Field: 'createdAt',
    parseV2Field: 'createdAt',
    fromParse: (value) => value ? new Date(value as string).toISOString() : undefined,
  },
  updatedAt: {
    uiField: 'updatedAt',
    parseV1Field: 'updatedAt',
    parseV2Field: 'updatedAt',
    fromParse: (value) => value ? new Date(value as string).toISOString() : undefined,
  },
};

/**
 * Parse Class Name
 */
export const MEMBER_CLASS_NAME = {
  v1: 'User',
  v2: 'Member',
} as const;













